/* eslint-disable react-hooks/set-state-in-effect */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertCircle,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  CircleDot,
  ClipboardCheck,
  Download,
  FileSpreadsheet,
  Flag,
  Info,
  UserRound,
  Palette,
  Plus,
  ShieldCheck,
  Sparkles,
  Trash2,
  Upload,
  Users,
  Wand2,
} from 'lucide-react';
import { PageShell } from '@/components/common/PageShell';
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeadCell,
  DataTableRow,
} from '@/components/common/DataTable';
import {
  Badge,
  Button,
  Card,
  DatePicker,
  Input,
  MultiSelect,
  Select,
  Spinner,
} from '@/components/ui';
import { SectionLoader } from '@/components/common/SectionLoader';
import type { MultiSelectOption, SelectOption } from '@/components/ui';
import { cn } from '@/lib/cn';
import { userSubline } from '@/lib/formatters';
import { useCompanies, useCompany } from '@/features/companies/hooks/useCompanies';
import { useCurrentOrganization } from '@/features/organization/hooks/useCurrentOrganization';
import { WEEKDAY_OPTIONS } from '@/features/organization/constants/settingsOptions';
import { useRoles } from '@/features/roles/hooks/useRoles';
import { useUsersList } from '@/features/users/hooks/useUsers';
import { usePermissions } from '@/hooks/usePermissions';
import { hexToRgba } from '@/features/shifts/utils/companyColor';
import type {
  CompanySetupStatus,
  ImportDuplicateRule,
  ImportJob,
  ImportPreviewRow,
  SetupDraftImport,
  SetupCompanyStatus,
  SetupHoliday,
  SetupProfile,
  SetupWizardStatus,
} from '@/types/setup-wizard.types';
import { NATIONALITY_OPTIONS } from '@/lib/nationalityOptions';
import {
  EMPTY_COMPANY_PROFILE,
  getCompanyProfileCompleteness,
  getCompanyProfileError,
  profileFromDraft,
  profileFromSettings,
  profileToDraft,
  type CompanyProfileFields,
} from '../utils/companyProfile';
import {
  useCheckSetupDraftImportRows,
  useCommitImportJob,
  useDownloadImportSetAside,
  useImportSetAside,
  useCreateImportDraft,
  useCreateSetupDraft,
  useDownloadEmployeeTemplate,
  useImportJob,
  useImportJobs,
  useLaunchSetupDraft,
  useSaveCompanySetup,
  useSaveImportMapping,
  useSetupDraft,
  useSetupWizardStatus,
  useUpdateSetupDraft,
  useUploadSetupDraftImport,
  useValidateImportJob,
} from '../hooks/useSetupWizard';

type WizardStepId = 'guide' | 'company' | 'operations' | 'import' | 'review';

interface WizardStep {
  id: WizardStepId;
  label: string;
  eyebrow: string;
  title: string;
  guide: string;
  outcome: string;
}

const wizardSteps: WizardStep[] = [
  {
    id: 'guide',
    label: 'Guide',
    eyebrow: 'Orientation',
    title: 'Prepare the company launch',
    guide:
      'Confirm what will be created, who needs access, and how the company becomes useful on day one.',
    outcome: 'A shared setup path with the required decisions visible before work begins.',
  },
  {
    id: 'company',
    label: 'Company',
    eyebrow: 'Identity',
    title: 'Create the company record',
    guide:
      'Capture name, generated code, wallboard color, status, managers, and starter members.',
    outcome: 'A company exists and can receive setup settings, assignments, and imports.',
  },
  {
    id: 'operations',
    label: 'Operations',
    eyebrow: 'Baseline',
    title: 'Set operating defaults',
    guide:
      'Define timezone, work week, and holidays before scheduling starts.',
    outcome: 'Attendance, scheduling, payroll, and imports share a consistent company baseline.',
  },
  {
    id: 'import',
    label: 'People import',
    eyebrow: 'Activation',
    title: 'Bring the first people in',
    guide:
      'Download a template, upload CSV/XLSX, map columns, check rows, then choose how to handle existing employees when needed.',
    outcome: 'Managers and members can start using the workspace with fewer manual edits.',
  },
  {
    id: 'review',
    label: 'Review',
    eyebrow: 'Launch',
    title: 'Check readiness and finish',
    guide: 'Resolve warnings, review the checklist, and finish when the company can operate.',
    outcome: 'The setup closes on the company detail page, where daily work continues.',
  },
];

const importFields = [
  ['firstName', 'First name'],
  ['lastName', 'Last name'],
  ['email', 'Email'],
  ['role', 'Role'],
  ['companyCode', 'Company code/name'],
  ['department', 'Department'],
  ['designation', 'Designation'],
  ['joiningDate', 'Joining date'],
  ['workLocation', 'Work location'],
  ['compensationType', 'Compensation type'],
  ['baseSalary', 'Base salary'],
  ['hourlyRate', 'Hourly rate'],
  ['phone', 'Phone'],
  ['personalEmail', 'Personal email'],
] as const;

type ImportFieldKey = (typeof importFields)[number][0];
type MappingState = 'ready' | 'review' | 'optional';

interface MappingInsight {
  field: ImportFieldKey;
  label: string;
  value: string;
  state: MappingState;
  message: string;
}

const requiredImportFields = new Set<ImportFieldKey>(['firstName', 'lastName', 'email']);
const importFieldIntent: Record<ImportFieldKey, string[]> = {
  firstName: ['firstname', 'first'],
  lastName: ['lastname', 'last', 'surname'],
  email: ['email', 'workemail'],
  role: ['role', 'permission', 'positiontype'],
  companyCode: ['companycode', 'company', 'branch'],
  department: ['department', 'team', 'division'],
  designation: ['designation', 'title', 'position'],
  joiningDate: ['joiningdate', 'startdate', 'hiredate', 'joined'],
  workLocation: ['worklocation', 'location', 'site'],
  compensationType: ['compensationtype', 'paytype', 'salarytype'],
  baseSalary: ['basesalary', 'salary'],
  hourlyRate: ['hourlyrate', 'rate'],
  phone: ['phone', 'mobile', 'contact'],
  personalEmail: ['personalemail', 'privateemail'],
};

const duplicateOptions: SelectOption[] = [
  { value: 'fail', label: 'Fail on existing email' },
  { value: 'skip_existing', label: 'Skip existing employees' },
  { value: 'update_existing', label: 'Update existing employees' },
];

const STATUS_OPTIONS: Array<{ value: SetupCompanyStatus; label: string; hint: string }> = [
  { value: 'active', label: 'Active', hint: 'Visible and usable across the app' },
  { value: 'inactive', label: 'Inactive', hint: 'Hidden from selection until reactivated' },
];

const defaultWorkWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

function isCompanySetupStatus(
  status: CompanySetupStatus | SetupWizardStatus | undefined
): status is CompanySetupStatus {
  return Boolean(status && 'company' in status);
}

function getManagerRoleId(roles: { _id: string; name?: string; slug?: string }[]): string | null {
  const manager = roles.find(
    (role) => role.slug?.toLowerCase() === 'manager' || role.name?.toLowerCase() === 'manager'
  );
  return manager?._id ?? null;
}

function normalizeCodePrefix(value?: string): string {
  const normalized = value?.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  return normalized || 'CO';
}

function nextCompanyCode(prefix: string, codes: Array<string | undefined>): string {
  const codePrefix = `${prefix}-`;
  let max = 0;
  for (const code of codes) {
    if (!code?.startsWith(codePrefix)) continue;
    const parsed = Number.parseInt(code.slice(codePrefix.length), 10);
    if (Number.isFinite(parsed) && parsed > max) max = parsed;
  }
  return `${codePrefix}${String(max + 1).padStart(3, '0')}`;
}

function isHoliday(value: unknown): value is SetupHoliday {
  if (!value || typeof value !== 'object') return false;
  const holiday = value as { name?: unknown; date?: unknown };
  return typeof holiday.name === 'string' && typeof holiday.date === 'string';
}

function normalizeHolidays(value: unknown): SetupHoliday[] {
  return Array.isArray(value) ? value.filter(isHoliday) : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function formatDateValue(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function normalizeHeader(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function normalizeHeaders(headers: unknown): string[] {
  return Array.isArray(headers) ? headers.map(String).filter(Boolean) : [];
}

function labelForImportField(field: ImportFieldKey): string {
  return importFields.find(([key]) => key === field)?.[1] ?? field;
}

function getMappedHeaderValue(row: ImportPreviewRow, field: ImportFieldKey, mapping: Record<string, string>): string {
  const column = mapping[field];
  if (!column) return '';
  return row.values[column] ?? '';
}

function buildInitialMapping(headers: unknown): Record<string, string> {
  const normalizedHeaders = normalizeHeaders(headers);
  const headerByKey = new Map(
    normalizedHeaders.map((header) => [normalizeHeader(header), header])
  );
  return Object.fromEntries(
    importFields.map(([field, label]) => [
      field,
      headerByKey.get(normalizeHeader(label)) ?? headerByKey.get(normalizeHeader(field)) ?? '',
    ])
  );
}

function hasMatchingIntent(field: ImportFieldKey, header: string): boolean {
  const normalizedHeader = normalizeHeader(header);
  return importFieldIntent[field].some((keyword) => normalizedHeader.includes(keyword));
}

function buildMappingInsights(
  mapping: Record<string, string>,
  defaultRoleId: string,
  defaultCompanyId: string
): MappingInsight[] {
  const usageCount = Object.values(mapping).reduce<Record<string, number>>((countByHeader, header) => {
    if (!header) return countByHeader;
    return { ...countByHeader, [header]: (countByHeader[header] ?? 0) + 1 };
  }, {});

  return importFields.map(([field, label]) => {
    const value = mapping[field] ?? '';
    const isRequired = requiredImportFields.has(field);
    const hasDefault =
      (field === 'role' && Boolean(defaultRoleId)) ||
      (field === 'companyCode' && Boolean(defaultCompanyId));

    if (!value && (isRequired || !hasDefault)) {
      return {
        field,
        label,
        value,
        state: isRequired ? 'review' : 'optional',
        message: isRequired ? 'Required field is missing' : 'Will use the selected default',
      };
    }

    if (!value) {
      return {
        field,
        label,
        value,
        state: 'optional',
        message: 'Not needed for this import',
      };
    }

    if (usageCount[value] > 1) {
      return {
        field,
        label,
        value,
        state: 'review',
        message: 'This workbook column is used more than once',
      };
    }

    if (!hasMatchingIntent(field, value)) {
      return {
        field,
        label,
        value,
        state: 'review',
        message: 'Column name looks unusual for this field',
      };
    }

    return {
      field,
      label,
      value,
      state: 'ready',
      message: 'Matched automatically',
    };
  });
}

function statusVariant(
  status: ImportJob['status']
): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  if (status === 'completed' || status === 'validated') return 'success';
  if (status === 'failed' || status === 'validation_failed') return 'danger';
  if (status === 'validating' || status === 'committing') return 'warning';
  if (status === 'mapped') return 'info';
  return 'neutral';
}

type ImportLike = ImportJob | SetupDraftImport;

function getImportId(job: ImportLike | null): string {
  if (!job) return '';
  return '_id' in job ? job._id : job.id;
}

function importTotals(job: ImportLike | null): ImportJob['totals'] {
  if (!isRecord(job?.totals)) {
    return {
      totalRows: 0,
      validRows: 0,
      invalidRows: 0,
      created: 0,
      updated: 0,
      linked: 0,
      skipped: 0,
    };
  }

  return {
    totalRows: Number(job.totals.totalRows) || 0,
    validRows: Number(job.totals.validRows) || 0,
    invalidRows: Number(job.totals.invalidRows) || 0,
    created: Number(job.totals.created) || 0,
    updated: Number(job.totals.updated) || 0,
    linked: Number(job.totals.linked) || 0,
    skipped: Number(job.totals.skipped) || 0,
  };
}

function importRowErrors(job: ImportLike | null): ImportJob['rowErrors'] {
  return Array.isArray(job?.rowErrors) ? job.rowErrors : [];
}

function isChecklistItemComplete(
  checklist: Array<{ key: string; completed: boolean }>,
  key: string
): boolean {
  return checklist.some((item) => item.key === key && item.completed);
}

function stepFromHash(hash: string): WizardStepId {
  const normalized = hash.replace('#', '');
  return wizardSteps.some((step) => step.id === normalized)
    ? (normalized as WizardStepId)
    : 'guide';
}

function StepIcon({ state }: { state: 'done' | 'current' | 'pending' }): React.ReactElement {
  if (state === 'done') return <CheckCircle2 className="h-4 w-4" aria-hidden />;
  if (state === 'current') return <CircleDot className="h-4 w-4" aria-hidden />;
  return <Circle className="h-4 w-4" aria-hidden />;
}

function rowResultVariant(
  status: string | undefined
): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  if (status === 'created' || status === 'added_to_company' || status === 'updated_user') {
    return 'success';
  }
  if (status === 'ready_new_user' || status === 'ready_existing_user') return 'info';
  if (status === 'existing_conflict') return 'warning';
  if (status === 'already_member' || status === 'blocked') return 'danger';
  if (status === 'skipped') return 'warning';
  return 'neutral';
}

function rowResultLabel(status: string | undefined): string {
  if (status === 'ready_new_user') return 'Will create';
  if (status === 'ready_existing_user') return 'Will add';
  if (status === 'existing_conflict') return 'Existing user';
  if (status === 'created') return 'Created';
  if (status === 'added_to_company') return 'Added';
  if (status === 'updated_user') return 'Updated';
  if (status === 'already_member') return 'Already member';
  if (status === 'blocked') return 'Blocked';
  if (status === 'skipped') return 'Skipped';
  return 'Pending';
}

function importStepLabel(status: ImportJob['status'], mappingDirty: boolean): string {
  if (status === 'completed') return 'Imported';
  if (status === 'committing') return 'Importing';
  if (status === 'validated' && !mappingDirty) return 'Ready to import';
  if (status === 'validating') return 'Checking rows';
  if (status === 'validation_failed') return 'Needs fixes';
  if (mappingDirty || status === 'draft') return 'Needs row check';
  return 'Mapping saved';
}

function getImportActionHint({
  hasMissingRequiredMapping,
  needsRowCheck,
  status,
}: {
  hasMissingRequiredMapping: boolean;
  needsRowCheck: boolean;
  status: ImportJob['status'];
}): string {
  if (hasMissingRequiredMapping) return 'Map the required columns before checking rows.';
  if (needsRowCheck) {
    return 'Check rows to confirm new users, existing users, and company membership before import.';
  }
  if (status === 'validation_failed') return 'Fix the blocked rows, then check rows again.';
  if (status === 'validated') return 'Rows are checked. Import users to add them to the system.';
  if (status === 'completed') return 'This file has already been imported.';
  return 'Upload a file to begin.';
}

export function SetupWizardPage(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, draftId } = useParams();
  const draftCreateStartedRef = useRef(false);
  const isCompanyScoped = Boolean(companyId);
  const isDraftSetup = !companyId;
  const [activeStep, setActiveStep] = useState<WizardStepId>(() => stepFromHash(location.hash));
  const { can, isLoading: permissionsLoading } = usePermissions();
  const canUpdateSetup = !permissionsLoading && can('import_setup', 'update');
  const canImportSetup = !permissionsLoading && can('import_setup', 'import');
  const canStartNewSetup =
    isCompanyScoped ||
    (!permissionsLoading &&
      can('companies', 'create') &&
      can('import_setup', 'read') &&
      can('import_setup', 'update'));

  const { data: organization } = useCurrentOrganization();
  const createDraft = useCreateSetupDraft();
  const activeDraftId = draftId === 'new' ? null : draftId ?? null;
  const { data: setupDraft, isLoading: draftLoading } = useSetupDraft(activeDraftId);
  const { data: company } = useCompany(companyId ?? null);
  const { data: status, isLoading: statusLoading } = useSetupWizardStatus(companyId);
  const companyStatus = isCompanySetupStatus(status) ? status : null;
  const organizationStatus = !isCompanySetupStatus(status) ? status : null;
  const { data: imports = [] } = useImportJobs(companyId, { enabled: Boolean(companyId) });
  const { data: roles = [], isLoading: rolesLoading } = useRoles();
  const { data: companies = [], isLoading: companiesLoading } = useCompanies();
  const managerRoleId = getManagerRoleId(roles);
  const roleNameById = useMemo(() => new Map(roles.map((role) => [role._id, role.name])), [roles]);
  const { data: usersData } = useUsersList({ limit: 100 });
  const users = usersData?.data ?? [];
  const { data: managersData } = useUsersList(
    { roleId: managerRoleId ?? undefined, status: 'active', limit: 100 },
    { enabled: !!managerRoleId }
  );
  const managers = managersData?.data ?? [];

  const [selectedImportId, setSelectedImportId] = useState<string | null>(null);
  const { data: selectedImport } = useImportJob(isDraftSetup ? null : selectedImportId);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [mappingDirty, setMappingDirty] = useState(false);
  const [duplicateRule, setDuplicateRule] = useState<ImportDuplicateRule>('fail');
  const [defaultRoleId, setDefaultRoleId] = useState('');
  const [defaultCompanyId, setDefaultCompanyId] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [workWeek, setWorkWeek] = useState<string[]>(defaultWorkWeek);
  const [holidays, setHolidays] = useState<SetupHoliday[]>([]);
  const [holidayName, setHolidayName] = useState('');
  const [holidayDate, setHolidayDate] = useState<Date | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [companyColor, setCompanyColor] = useState('');
  const [companyRecordStatus, setCompanyRecordStatus] = useState<SetupCompanyStatus>('active');
  const [managerIds, setManagerIds] = useState<string[]>([]);
  const [initialMemberIds, setInitialMemberIds] = useState<string[]>([]);
  const [profile, setProfile] = useState<CompanyProfileFields>(EMPTY_COMPANY_PROFILE);
  const setProfileField = (field: keyof CompanyProfileFields, value: string): void =>
    setProfile((current) => ({ ...current, [field]: value }));
  const profileCompleteness = getCompanyProfileCompleteness(profile);
  const profileError = getCompanyProfileError(profile);

  const saveCompanySetup = useSaveCompanySetup();
  const downloadTemplate = useDownloadEmployeeTemplate(
    companyId,
    activeDraftId ?? undefined,
    companyCode
  );
  const createImport = useCreateImportDraft(companyId);
  const saveMapping = useSaveImportMapping();
  const validateImport = useValidateImportJob();
  const commitImport = useCommitImportJob();
  const importSetAside = useImportSetAside(isDraftSetup ? activeDraftId : null);
  const downloadSetAside = useDownloadImportSetAside();
  const updateDraft = useUpdateSetupDraft();
  const uploadDraftImport = useUploadSetupDraftImport(activeDraftId);
  const checkDraftRows = useCheckSetupDraftImportRows(activeDraftId);
  const launchDraft = useLaunchSetupDraft();

  const codePrefix = normalizeCodePrefix(organization?.settings?.companyCodePrefix);
  const suggestedCode = useMemo(
    () => nextCompanyCode(codePrefix, companies.map((item) => item.code)),
    [codePrefix, companies]
  );
  const pickerColor = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(companyColor)
    ? companyColor
    : '#6366f1';
  const companyAccent = companyColor.trim() || '#6366f1';
  const profileReady = companyName.trim().length > 0 && companyCode.trim().length > 0;
  const draftImports = setupDraft?.peopleDraft?.imports ?? [];
  const selectedDraftImport =
    selectedImportId ? draftImports.find((item) => item.id === selectedImportId) : null;
  const canImportMembers = (Boolean(companyId) || Boolean(activeDraftId)) && canImportSetup;
  const activeImport: ImportLike | null = selectedDraftImport ?? selectedImport ?? draftImports[0] ?? imports[0] ?? null;
  const activeImportTotals = importTotals(activeImport);
  const activeImportErrors = importRowErrors(activeImport);
  const hasExistingImportUsers = Boolean(
    activeImport?.validatedAt &&
      activeImport.rowResults?.some(
        (result) =>
          result.status === 'ready_existing_user' || result.status === 'existing_conflict'
      )
  );
  const activeImportStatus = activeImport?.status ?? 'draft';
  const activeImportFilename = activeImport?.originalFilename ?? 'Import draft';
  const activeImportCompleted = activeImportStatus === 'completed';
  const activeImportLocked =
    activeImportStatus === 'completed' ||
    activeImportStatus === 'committing' ||
    activeImportStatus === 'validating';
  const mappingSaved = Boolean(activeImport) && activeImportStatus !== 'draft' && !mappingDirty;
  const canCommit = activeImportStatus === 'validated' && activeImportErrors.length === 0;
  // Rows, not problems: one row can have several.
  const failingRowCount = new Set(activeImportErrors.map((error) => error.row)).size;
  const activeSetAsideRows = activeImport?.setAsideRows ?? [];
  // Setting every row aside would leave nothing to import.
  const canSetAsideRows =
    activeImportStatus === 'validation_failed' &&
    failingRowCount > 0 &&
    failingRowCount < activeImportTotals.totalRows;
  const changeSetAside = (action: 'set-aside-failed' | 'restore-set-aside'): void => {
    if (!activeImport) return;
    importSetAside.mutate({ importId: getImportId(activeImport), action });
  };
  const downloadSetAsideRows = (): void => {
    if (!activeImport) return;
    downloadSetAside.mutate({
      importId: getImportId(activeImport),
      draftId: isDraftSetup ? activeDraftId : null,
      filename: activeImport.originalFilename,
    });
  };
  const mappingInsights = useMemo(
    () => buildMappingInsights(mapping, defaultRoleId, defaultCompanyId),
    [defaultCompanyId, defaultRoleId, mapping]
  );
  const mappingsReady = mappingInsights.filter((item) => item.state === 'ready');
  const mappingsToReview = mappingInsights.filter((item) => item.state === 'review');
  const optionalMappings = mappingInsights.filter((item) => item.state === 'optional');
  const canSaveImportMapping = Boolean(activeImport) && canImportSetup && !activeImportLocked;
  const hasMissingRequiredMapping = mappingsToReview.some(
    (item) => requiredImportFields.has(item.field) && !item.value
  );
  const canCheckRows =
    Boolean(activeImport) && canSaveImportMapping && !hasMissingRequiredMapping;
  const needsRowCheck =
    Boolean(activeImport) &&
    !activeImportCompleted &&
    (mappingDirty || activeImportStatus === 'draft' || activeImportStatus === 'mapped');
  const importActionHint = getImportActionHint({
    hasMissingRequiredMapping,
    needsRowCheck,
    status: activeImportStatus,
  });
  const previewRows = activeImport?.previewRows ?? [];
  // The React Compiler memoizes this; a manual useMemo here could not be kept.
  const rowResultsByRow = new Map(
    (activeImport?.rowResults ?? []).map((result) => [result.row, result])
  );
  const invalidRowCount = activeImportTotals.invalidRows || activeImportErrors.length;
  const importReadyCount =
    activeImportTotals.validRows || Math.max(activeImportTotals.totalRows - invalidRowCount, 0);
  const completedImports = imports.filter((job) => job.status === 'completed');
  const checkedDraftImports = draftImports.filter((job) => job.status === 'validated');
  const existingCompanyMemberCount = companyStatus?.counts.members ?? company?.memberCount ?? 0;
  const importedPeopleCount = completedImports.reduce((total, job) => {
    const totals = importTotals(job);
    return total + totals.created + totals.updated + totals.linked + totals.skipped;
  }, 0) + checkedDraftImports.reduce((total, job) => {
    const totals = importTotals(job);
    return total + totals.created + totals.linked;
  }, 0);
  const checklist = companyStatus?.checklist ?? organizationStatus?.checklist ?? [];
  const completedDraftSteps = new Set(setupDraft?.completedSteps ?? []);
  const companySaved = Boolean(companyId) || Boolean(activeDraftId);
  const companyProfileSaved =
    companySaved &&
    (isDraftSetup ? completedDraftSteps.has('company') && profileReady : isChecklistItemComplete(checklist, 'company_profile') || profileReady);
  const companyCodeSaved =
    companySaved &&
    (isDraftSetup ? completedDraftSteps.has('company') && Boolean(companyCode) : isChecklistItemComplete(checklist, 'company_code') || Boolean(companyCode));
  const operationsReviewed =
    companySaved &&
    (isDraftSetup ? completedDraftSteps.has('operations') : isChecklistItemComplete(checklist, 'settings_reviewed'));
  const peoplePrepared =
    companySaved &&
    ((isDraftSetup ? completedDraftSteps.has('people') : isChecklistItemComplete(checklist, 'members_added')) ||
      existingCompanyMemberCount > 0 ||
      initialMemberIds.length > 0 ||
      importedPeopleCount > 0);
  const wizardProgressItems = [
    { key: 'guide', label: 'Guide', completed: companySaved || activeStep !== 'guide' },
    { key: 'company', label: 'Company', completed: companyProfileSaved && companyCodeSaved },
    { key: 'operations', label: 'Operations', completed: operationsReviewed },
    { key: 'import', label: 'People import', completed: peoplePrepared },
    {
      key: 'review',
      label: 'Review',
      completed: isDraftSetup
        ? completedDraftSteps.has('review')
        : companyStatus?.progress.state === 'ready',
    },
  ];
  const progressCompleted = wizardProgressItems.filter((item) => item.completed).length;
  const progressTotal = wizardProgressItems.length;
  const progressPercent = Math.round((progressCompleted / progressTotal) * 100);
  const stepCompletedById: Record<WizardStepId, boolean> = {
    guide: activeStep !== 'guide' || progressCompleted > 0,
    company: companyProfileSaved && companyCodeSaved,
    operations: operationsReviewed,
    import: peoplePrepared,
    review: false,
  };
  const activeStepIndex = wizardSteps.findIndex((step) => step.id === activeStep);
  const activeStepConfig = wizardSteps[activeStepIndex] ?? wizardSteps[0];
  const setupPath = companyId
    ? `/companies/${companyId}/setup-wizard`
    : activeDraftId
      ? `/companies/setup-wizard/drafts/${activeDraftId}`
      : '/companies/setup-wizard/drafts/new';
  const organizationOptions: SelectOption[] = useMemo(() => {
    const options = new Map<string, SelectOption>();
    if (organization) {
      options.set(organization._id, {
        value: organization._id,
        label: organization.name,
        description: organization.slug,
      });
    }
    if (organizationStatus) {
      options.set(organizationStatus.organization.id, {
        value: organizationStatus.organization.id,
        label: organizationStatus.organization.name,
        description: organizationStatus.organization.slug,
      });
    }
    return Array.from(options.values());
  }, [organization, organizationStatus]);

  const userOptions: MultiSelectOption[] = users.map((user) => ({
    value: user._id,
    label:
      user.name || [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email,
    description: [user.roleName ?? roleNameById.get(user.roleId), userSubline(user.employeeId, user.email)]
      .filter(Boolean)
      .join(' · '),
  }));
  const managerOptions: MultiSelectOption[] = managers.map((user) => ({
    value: user._id,
    label:
      user.name || [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email,
    description: [user.roleName ?? roleNameById.get(user.roleId), userSubline(user.employeeId, user.email)]
      .filter(Boolean)
      .join(' · '),
  }));
  const managersSelectDisabled = rolesLoading || !managerRoleId || isCompanyScoped;
  const managersPlaceholder = rolesLoading
    ? 'Loading managers...'
    : isCompanyScoped
      ? 'Use company members after setup'
      : !managerRoleId
        ? 'No manager role found'
        : 'Search and select managers';
  const headerOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: 'Not mapped' },
      ...normalizeHeaders(selectedImport?.headers).map((header) => ({ value: header, label: header })),
    ],
    [selectedImport?.headers]
  );
  const roleOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: 'Use mapped role' },
      ...roles.map((role) => ({ value: role._id, label: role.name })),
    ],
    [roles]
  );
  const defaultUserRoleId = useMemo(() => {
    const role = roles.find((item) => {
      const slug = item.slug?.toLowerCase();
      const name = item.name?.toLowerCase();
      return slug === 'user' || name === 'user' || slug === 'staff' || name === 'staff';
    });
    return role?._id ?? '';
  }, [roles]);
  const companyOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: isCompanyScoped ? 'Selected company' : 'Use mapped company' },
      ...companies.map((item) => ({
        value: item._id,
        label: item.code ? `${item.name} (${item.code})` : item.name,
      })),
    ],
    [companies, isCompanyScoped]
  );

  useEffect(() => {
    setActiveStep(stepFromHash(location.hash));
  }, [location.hash]);

  useEffect(() => {
    if (
      !isDraftSetup ||
      draftId !== 'new' ||
      createDraft.isPending ||
      permissionsLoading ||
      draftCreateStartedRef.current
    ) {
      return;
    }
    if (!canStartNewSetup) return;
    draftCreateStartedRef.current = true;
    createDraft.mutate(
      { currentStep: 'guide', completedSteps: [] },
      {
        onSuccess: (draft) => {
          navigate(`/companies/setup-wizard/drafts/${draft._id}${location.hash || '#guide'}`, {
            replace: true,
          });
        },
        onError: () => {
          draftCreateStartedRef.current = false;
        },
      }
    );
  }, [
    canStartNewSetup,
    createDraft,
    draftId,
    isDraftSetup,
    location.hash,
    navigate,
    permissionsLoading,
  ]);

  useEffect(() => {
    if (!organizationStatus) return;
    setSelectedOrganizationId(organizationStatus.organization.id);
    const settings = organizationStatus.organization.settings;
    setTimezone(typeof settings.timezone === 'string' ? settings.timezone : 'UTC');
    setWorkWeek(Array.isArray(settings.workWeek) ? settings.workWeek.map(String) : defaultWorkWeek);
    setHolidays(normalizeHolidays(settings.holidays));
  }, [organizationStatus]);

  useEffect(() => {
    if (organizationStatus || !organization) return;
    setSelectedOrganizationId(organization._id);
  }, [organization, organizationStatus]);

  useEffect(() => {
    if (!company) return;
    setCompanyName(company.name);
    setCompanyCode(company.code ?? '');
    setCompanyColor(company.color ?? '');
    setCompanyRecordStatus(company.status === 'inactive' ? 'inactive' : 'active');
    setDefaultCompanyId(company._id);
    const settings = company.settings ?? {};
    if (typeof settings.timezone === 'string') setTimezone(settings.timezone);
    if (Array.isArray(settings.workWeek)) setWorkWeek(settings.workWeek.map(String));
    setHolidays(normalizeHolidays(settings.holidays));
    setProfile(profileFromSettings(settings));
  }, [company]);

  useEffect(() => {
    if (!setupDraft) return;
    const companyDraft = setupDraft.companyDraft ?? {};
    const operationsDraft = setupDraft.operationsDraft ?? {};
    const peopleDraft = setupDraft.peopleDraft ?? {};
    setCompanyName(companyDraft.name ?? '');
    setCompanyCode(companyDraft.code ?? '');
    setCompanyColor(companyDraft.color ?? '');
    setCompanyRecordStatus(companyDraft.status === 'inactive' ? 'inactive' : 'active');
    setManagerIds(peopleDraft.managerIds ?? companyDraft.managerIds ?? []);
    setInitialMemberIds(peopleDraft.initialMemberIds ?? companyDraft.initialMemberIds ?? []);
    if (operationsDraft.timezone) setTimezone(operationsDraft.timezone);
    if (Array.isArray(operationsDraft.workWeek)) setWorkWeek(operationsDraft.workWeek.map(String));
    if (Array.isArray(operationsDraft.holidays)) setHolidays(normalizeHolidays(operationsDraft.holidays));
    setProfile(profileFromDraft(setupDraft.profileDraft));
    const latestImport = peopleDraft.imports?.[0];
    if (latestImport && !selectedImportId) setSelectedImportId(latestImport.id);
  }, [selectedImportId, setupDraft]);

  useEffect(() => {
    if (isCompanyScoped || companiesLoading || companyCode.trim()) return;
    setCompanyCode(suggestedCode);
  }, [companiesLoading, companyCode, isCompanyScoped, suggestedCode]);

  useEffect(() => {
    if (!selectedImport) return;
    setMapping(
      Object.keys(selectedImport.mapping ?? {}).length > 0
        ? selectedImport.mapping
        : buildInitialMapping(selectedImport.headers)
    );
    setDuplicateRule(selectedImport.duplicateRule ?? 'fail');
    setDefaultRoleId(selectedImport.defaultRoleId ?? defaultUserRoleId);
    setDefaultCompanyId(companyId ?? selectedImport.defaultCompanyId ?? '');
    setMappingDirty(selectedImport.status === 'draft' || !selectedImport.defaultRoleId);
  }, [companyId, defaultUserRoleId, selectedImport]);

  useEffect(() => {
    if (!selectedDraftImport) return;
    setMapping(
      Object.keys(selectedDraftImport.mapping ?? {}).length > 0
        ? selectedDraftImport.mapping
        : buildInitialMapping(selectedDraftImport.headers)
    );
    setDuplicateRule(selectedDraftImport.duplicateRule ?? 'fail');
    setDefaultRoleId(selectedDraftImport.defaultRoleId ?? defaultUserRoleId);
    setDefaultCompanyId(selectedDraftImport.defaultCompanyId ?? '');
    setMappingDirty(selectedDraftImport.status === 'draft' || !selectedDraftImport.defaultRoleId);
  }, [defaultUserRoleId, selectedDraftImport]);

  const getStepLockReason = useCallback((step: WizardStepId): string | null => {
    if (step === 'guide' || step === 'company') return null;
    if (!isCompanyScoped && !profileReady) return 'Complete the company name and code first.';
    if (!companyId && !activeDraftId) return 'Start the setup draft before continuing.';
    if (step === 'import' && !canImportSetup) return 'People import requires import setup access.';
    if ((step === 'operations' || step === 'review') && !canUpdateSetup) {
      return 'Saving setup details requires setup update access.';
    }
    return null;
  }, [activeDraftId, canImportSetup, canUpdateSetup, companyId, isCompanyScoped, profileReady]);

  useEffect(() => {
    const lockReason = getStepLockReason(activeStep);
    if (!lockReason || activeStep === 'company') return;
    setActiveStep('company');
    window.history.replaceState(null, '', `${setupPath}#company`);
    toast.info(lockReason);
  }, [activeStep, getStepLockReason, setupPath]);

  if (!permissionsLoading && !canStartNewSetup) {
    return <Navigate to="/companies" replace />;
  }

  if (isDraftSetup && (draftId === 'new' || draftLoading || createDraft.isPending) && !setupDraft) {
    return (
      <PageShell
        breadcrumbItems={[{ label: 'Companies', to: '/companies' }, { label: 'Setup Wizard' }]}
        title="Setup Wizard"
        description="Preparing your company setup draft."
      >
        <div className="space-y-4">
          <WorkspaceToolbar />
          <Card className="setup-wizard-material" contentClassName="flex items-center gap-3 p-6">
            <Spinner />
            <span className="text-sm text-gray-600 dark:text-gray-300">Starting setup draft...</span>
          </Card>
        </div>
      </PageShell>
    );
  }

  const goToStep = (step: WizardStepId): void => {
    const lockReason = getStepLockReason(step);
    if (lockReason) {
      toast.info(lockReason);
      return;
    }
    setActiveStep(step);
    window.history.replaceState(null, '', `${setupPath}#${step}`);
  };

  const addHoliday = (): void => {
    if (!holidayDate) return;
    const date = formatDateValue(holidayDate);
    const name = holidayName.trim();
    if (!name) return;
    setHolidays((current) =>
      [...current.filter((holiday) => holiday.date !== date), { name, date }].sort((a, b) =>
        a.date.localeCompare(b.date)
      )
    );
    setHolidayName('');
    setHolidayDate(null);
  };

  const removeHoliday = (date: string): void => {
    setHolidays((current) => current.filter((holiday) => holiday.date !== date));
  };

  const buildDraftPayload = (
    nextStep?: WizardStepId
  ): {
    currentStep?: 'guide' | 'company' | 'operations' | 'people' | 'review';
    completedSteps: Array<'guide' | 'company' | 'operations' | 'people' | 'review'>;
    companyDraft: {
      name: string;
      code?: string;
      color?: string;
      status: SetupCompanyStatus;
      managerIds: string[];
      initialMemberIds: string[];
    };
    profileDraft: SetupProfile;
    operationsDraft: {
      timezone: string;
      workWeek: string[];
      holidays: SetupHoliday[];
    };
    peopleDraft: {
      managerIds: string[];
      initialMemberIds: string[];
    };
  } => ({
    currentStep: nextStep === 'import' ? 'people' : nextStep,
    completedSteps: Array.from(
      new Set([
        ...(setupDraft?.completedSteps ?? []),
        activeStep === 'import' ? 'people' : activeStep,
      ])
    ) as Array<'guide' | 'company' | 'operations' | 'people' | 'review'>,
    companyDraft: {
      name: companyName.trim(),
      code: companyCode.trim().toUpperCase() || undefined,
      color: companyColor.trim() || undefined,
      status: companyRecordStatus,
      managerIds,
      initialMemberIds,
    },
    profileDraft: profileToDraft(profile),
    operationsDraft: {
      timezone,
      workWeek,
      holidays,
    },
    peopleDraft: {
      managerIds,
      initialMemberIds,
    },
  });

  const saveSetup = (nextStep?: WizardStepId, finish = false): void => {
    if (isDraftSetup) {
      const payload = buildDraftPayload(nextStep);
      if (!activeDraftId) {
        createDraft.mutate(payload, {
          onSuccess: (draft) => {
            const targetStep = nextStep ?? 'company';
            navigate(`/companies/setup-wizard/drafts/${draft._id}#${targetStep}`, {
              replace: true,
            });
          },
        });
        return;
      }
      updateDraft.mutate(
        {
          id: activeDraftId,
          payload,
        },
        {
          onSuccess: () => {
            if (finish) {
              launchDraft.mutate(activeDraftId, {
                onSuccess: (result) => {
                  navigate(`/companies/${result.companyId}`, { replace: true });
                },
              });
              return;
            }
            if (nextStep) goToStep(nextStep);
          },
        }
      );
      return;
    }

    const includeOperations = nextStep !== 'operations';
    saveCompanySetup.mutate(
      {
        timezone: includeOperations ? timezone : undefined,
        workWeek: includeOperations ? workWeek : undefined,
        holidays: includeOperations ? holidays : undefined,
        companyId,
        companies: [
          {
            id: companyId,
            name: companyName.trim(),
            code: companyCode.trim().toUpperCase() || undefined,
            color: companyColor.trim() || undefined,
            status: companyRecordStatus,
            managerIds: !isCompanyScoped && managerIds.length > 0 ? managerIds : undefined,
            initialMemberIds:
              !isCompanyScoped && initialMemberIds.length > 0 ? initialMemberIds : undefined,
            profile: profileToDraft(profile),
          },
        ],
      },
      {
        onSuccess: (result) => {
          const nextCompanyId = companyId ?? result.companies[0]?.id;
          if (finish && nextCompanyId) {
            navigate(`/companies/${nextCompanyId}`, { replace: true });
            return;
          }
          if (!companyId && nextCompanyId) {
            navigate(`/companies/${nextCompanyId}/setup-wizard#${nextStep ?? 'operations'}`, {
              replace: true,
            });
            return;
          }
          if (nextStep) goToStep(nextStep);
        },
      }
    );
  };

  const saveAndValidateImport = (): void => {
    if (!activeImport || !canSaveImportMapping || hasMissingRequiredMapping) return;
    const payload = { mapping, duplicateRule, defaultRoleId, defaultCompanyId };
    const activeImportId = getImportId(activeImport);
    if (isDraftSetup) {
      checkDraftRows.mutate(
        { importId: activeImportId, payload },
        {
          onSuccess: () => {
            setMappingDirty(false);
          },
        }
      );
      return;
    }
    if (mappingDirty || !mappingSaved) {
      saveMapping.mutate(
        { id: activeImportId, payload },
        {
          onSuccess: (job) => {
            setMappingDirty(false);
            validateImport.mutate(job._id);
          },
        }
      );
      return;
    }
    validateImport.mutate(activeImportId);
  };

  const uploadAndPreflightImport = (file: File): void => {
    if (isDraftSetup) {
      if (!activeDraftId) return;
      uploadDraftImport.mutate(file, {
        onSuccess: (draft) => {
          const latestImport = draft.peopleDraft?.imports?.at(-1);
          if (!latestImport) return;
          setSelectedImportId(latestImport.id);
          setMapping(
            Object.keys(latestImport.mapping ?? {}).length > 0
              ? latestImport.mapping
              : buildInitialMapping(latestImport.headers)
          );
          setDuplicateRule(latestImport.duplicateRule ?? 'fail');
          setDefaultRoleId(latestImport.defaultRoleId ?? defaultUserRoleId);
          setDefaultCompanyId(latestImport.defaultCompanyId ?? '');
          setMappingDirty(false);
        },
      });
      return;
    }
    createImport.mutate(file, {
      onSuccess: (job) => {
        const initialMapping = buildInitialMapping(job.headers);
        const roleId = job.defaultRoleId ?? defaultUserRoleId;
        const selectedCompanyId = companyId ?? job.defaultCompanyId ?? '';
        setSelectedImportId(job._id);
        setMapping(initialMapping);
        setDuplicateRule(job.duplicateRule ?? 'fail');
        setDefaultRoleId(roleId);
        setDefaultCompanyId(selectedCompanyId);
        saveMapping.mutate(
          {
            id: job._id,
            payload: {
              mapping: initialMapping,
              duplicateRule: job.duplicateRule ?? 'fail',
              defaultRoleId: roleId,
              defaultCompanyId: selectedCompanyId,
            },
          },
          {
            onSuccess: (mappedJob) => {
              setMappingDirty(false);
              validateImport.mutate(mappedJob._id);
            },
          }
        );
      },
    });
  };

  const runImportPrimaryAction = (): void => {
    if (!activeImport) return;
    const activeImportId = getImportId(activeImport);
    if (!isDraftSetup && canCommit) {
      commitImport.mutate(activeImportId);
      return;
    }
    saveAndValidateImport();
  };

  const reviewWarnings = [
    !profileReady ? 'Company name and code are required before launch.' : null,
    workWeek.length === 0 ? 'Choose at least one working day.' : null,
    !companyId && !activeDraftId ? 'Start the draft before importing people.' : null,
    isDraftSetup && activeImport && activeImportErrors.length > 0
      ? 'Imported rows have blocking errors.'
      : null,
    isDraftSetup && activeImport && activeImportStatus !== 'validated'
      ? 'Check imported rows before launch.'
      : null,
    existingCompanyMemberCount === 0 && importedPeopleCount === 0 && initialMemberIds.length === 0
      ? 'No people have been assigned or imported yet. You can still finish and add them later.'
      : null,
    // Detail fields stay optional, so this is a nudge only — it is deliberately
    // not part of canLaunchCompany below.
    profileCompleteness.missing.length > 0
      ? `${profileCompleteness.missing.length} company detail ${
          profileCompleteness.missing.length === 1 ? 'field is' : 'fields are'
        } still empty (${profileCompleteness.missing.join(', ')}). You can launch now and add them later.`
      : null,
  ].filter((warning): warning is string => Boolean(warning));
  const canLaunchCompany =
    profileReady &&
    workWeek.length > 0 &&
    canUpdateSetup &&
    (!isDraftSetup ||
      (Boolean(activeDraftId) &&
        !launchDraft.isPending &&
        draftImports.every((job) => job.status === 'validated' && job.rowErrors.length === 0)));
  const draftImportTotals = draftImports.reduce(
    (totals, job) => {
      const itemTotals = importTotals(job);
      return {
        newUsers: totals.newUsers + itemTotals.created,
        existingUsers: totals.existingUsers + itemTotals.linked,
        blockedRows: totals.blockedRows + itemTotals.invalidRows,
      };
    },
    { newUsers: 0, existingUsers: 0, blockedRows: 0 }
  );

  const renderGuideStep = (): React.ReactElement => (
    <Card className="setup-wizard-material" contentClassName="p-6 sm:p-7">
      <div className="flex flex-col gap-5 border-b border-gray-200 pb-6 dark:border-gray-700 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20 dark:text-blue-200">
              <Wand2 className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                Setup draft
              </p>
              <h2 className="text-xl font-semibold tracking-normal text-gray-950 dark:text-gray-50">
                Prepare first, launch once
              </h2>
            </div>
          </div>
          <p className="mt-4 text-sm leading-6 text-gray-600 dark:text-gray-300">
            Build the company profile, operating defaults, and people list as a saved draft. Nothing
            is added to the live system until the final launch.
          </p>
        </div>
        <Button className="setup-wizard-action shrink-0" onClick={() => goToStep('company')}>
          Start company profile
          <ChevronRight className="ml-2 h-4 w-4" aria-hidden />
        </Button>
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-3">
        {[
          ['Saved draft', 'Resume after logout without creating unused records.'],
          ['Row checks', 'See new users, existing users, and blocked rows before launch.'],
          ['One launch', 'Create the company, settings, members, and import history together.'],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded-xl border border-gray-200 bg-gray-50/70 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/30"
          >
            <p className="text-sm font-semibold text-gray-950 dark:text-gray-100">{label}</p>
            <p className="mt-1 text-sm leading-5 text-gray-500 dark:text-gray-400">{value}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="grid gap-0 md:grid-cols-4">
          {wizardSteps.slice(1).map((step, index) => (
            <div
              key={step.id}
              className="border-b border-gray-200 p-4 last:border-b-0 dark:border-gray-700 md:border-b-0 md:border-r md:last:border-r-0"
            >
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {index + 1}
                </span>
                <p className="text-sm font-semibold text-gray-950 dark:text-gray-100">
                  {step.label}
                </p>
              </div>
              <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
                {step.outcome}
              </p>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );

  const renderCompanyStep = (): React.ReactElement => (
    <Card className="setup-wizard-material" contentClassName="p-0">
      <div className="flex flex-col gap-4 border-b border-gray-200 p-6 dark:border-gray-700 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
            <Building2 className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-950 dark:text-gray-100">
              Company profile
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
              Name the company, choose its code and accent, add its contact and location details,
              then optionally assign starter people.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Badge variant={companyRecordStatus === 'active' ? 'success' : 'neutral'}>
            {companyRecordStatus}
          </Badge>
          <Badge variant="neutral">{managerIds.length} managers</Badge>
          <Badge variant="neutral">{initialMemberIds.length} people</Badge>
        </div>
      </div>

      <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          <section className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" aria-hidden />
              <h3 className="text-sm font-semibold text-gray-950 dark:text-gray-100">
                Identity
              </h3>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {!isCompanyScoped ? (
                <Select
                  label="Organization"
                  options={organizationOptions}
                  value={selectedOrganizationId}
                  onChange={setSelectedOrganizationId}
                  placeholder="Choose organization"
                  disabled={organizationOptions.length <= 1}
                />
              ) : null}
              <Input
                label="Name"
                required
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
                placeholder="e.g. Acme Healthcare"
              />
              <Input
                label="Code"
                required
                value={companyCode}
                onChange={(event) => setCompanyCode(event.target.value.toUpperCase())}
                placeholder={suggestedCode}
                hint={`Auto-suggested from organization prefix ${codePrefix}.`}
              />
            </div>
          </section>

          <section className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <Palette className="h-4 w-4 text-primary dark:text-primary-light" aria-hidden />
              <h3 className="text-sm font-semibold text-gray-950 dark:text-gray-100">
                Appearance
              </h3>
            </div>
            <div className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)] md:items-end">
              <div className="flex items-end gap-3">
                <label className="flex h-11 w-12 cursor-pointer items-center justify-center rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                  <span className="sr-only">Pick wallboard color</span>
                  <input
                    type="color"
                    value={pickerColor}
                    onChange={(event) => setCompanyColor(event.target.value)}
                    className="h-8 w-8 cursor-pointer border-0 bg-transparent p-0"
                  />
                </label>
                <Input
                  label="Hex colour"
                  value={companyColor}
                  onChange={(event) => setCompanyColor(event.target.value)}
                  placeholder="#6366f1"
                />
              </div>
              <div
                className="rounded-xl border px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100"
                style={{
                  backgroundColor: companyColor ? hexToRgba(companyColor, 0.1) : undefined,
                  borderColor: companyColor || undefined,
                }}
              >
                Wallboard card preview
              </div>
            </div>
          </section>

          <section className="p-6">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <UserRound className="h-4 w-4 text-sky-600 dark:text-sky-300" aria-hidden />
                <h3 className="text-sm font-semibold text-gray-950 dark:text-gray-100">
                  Company details
                </h3>
                <Badge variant="neutral" size="sm">
                  Optional
                </Badge>
              </div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {profileCompleteness.completed} of {profileCompleteness.total} added
              </span>
            </div>
            <p className="mb-4 text-sm leading-6 text-gray-500 dark:text-gray-400">
              These appear on the company&apos;s detail page. You can continue without them and
              fill them in later from company settings.
            </p>

            <div
              className="mb-5 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
              role="progressbar"
              aria-valuenow={profileCompleteness.completed}
              aria-valuemin={0}
              aria-valuemax={profileCompleteness.total}
              aria-label="Company details completeness"
            >
              <div
                className={cn(
                  'h-full rounded-full transition-[width]',
                  profileCompleteness.missing.length === 0 ? 'bg-success' : 'bg-primary'
                )}
                style={{ width: `${profileCompleteness.percent}%` }}
              />
            </div>

            <div className="space-y-5">
              <div>
                <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  Site contact
                </h4>
                <div className="grid gap-4 md:grid-cols-3">
                  <Input
                    label="Contact name"
                    value={profile.contactName}
                    onChange={(event) => setProfileField('contactName', event.target.value)}
                    placeholder="e.g. Sara Ahmed"
                  />
                  <Input
                    label="Contact email"
                    type="email"
                    value={profile.contactEmail}
                    onChange={(event) => setProfileField('contactEmail', event.target.value)}
                    placeholder="contact@example.com"
                    error={profileError ?? undefined}
                  />
                  <Input
                    label="Contact phone"
                    value={profile.contactPhone}
                    onChange={(event) => setProfileField('contactPhone', event.target.value)}
                    placeholder="+92 300 0000000"
                  />
                </div>
              </div>

              <div>
                <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  Location
                </h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label="Street"
                    value={profile.street}
                    onChange={(event) => setProfileField('street', event.target.value)}
                  />
                  <Input
                    label="City"
                    value={profile.city}
                    onChange={(event) => setProfileField('city', event.target.value)}
                  />
                  <Input
                    label="State"
                    value={profile.state}
                    onChange={(event) => setProfileField('state', event.target.value)}
                  />
                  <Select
                    label="Country"
                    options={NATIONALITY_OPTIONS}
                    value={profile.country}
                    onChange={(value) => setProfileField('country', value)}
                    placeholder="Select country"
                    searchable
                    searchPlaceholder="Search countries..."
                  />
                  <Input
                    label="Postal code"
                    value={profile.postalCode}
                    onChange={(event) => setProfileField('postalCode', event.target.value)}
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Timezone is set on the next step, with the work week and holidays.
                </p>
              </div>

              <div>
                <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  Client instructions
                </h4>
                <textarea
                  value={profile.clientInstructions}
                  onChange={(event) => setProfileField('clientInstructions', event.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-primary/40 focus:ring-2 focus:ring-primary/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                  placeholder="Site rules, parking, dress expectations, reporting steps..."
                />
              </div>

              {profileCompleteness.missing.length > 0 ? (
                <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800/60">
                  <Info
                    className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
                    aria-hidden
                  />
                  <p className="text-gray-600 dark:text-gray-300">
                    Still empty: {profileCompleteness.missing.join(', ')}. You can continue and add
                    these later.
                  </p>
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm dark:border-success/30">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
                  <p className="text-gray-700 dark:text-gray-200">
                    All company details are filled in.
                  </p>
                </div>
              )}
            </div>
          </section>

          <section className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <Flag className="h-4 w-4 text-orange-600 dark:text-orange-300" aria-hidden />
              <h3 className="text-sm font-semibold text-gray-950 dark:text-gray-100">
                Availability
              </h3>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setCompanyRecordStatus(option.value)}
                  className={cn(
                    'setup-wizard-action flex items-center gap-3 rounded-xl border p-4 text-left transition-[transform,filter,border-color,background-color] active:scale-[0.99]',
                    companyRecordStatus === option.value
                      ? 'border-primary bg-primary/5 ring-2 ring-primary/20 dark:bg-primary/15'
                      : 'border-gray-200 bg-white/80 hover:border-primary/30 hover:bg-white dark:border-gray-700 dark:bg-gray-800/80'
                  )}
                >
                  <CircleDot
                    className={cn(
                      'h-5 w-5 shrink-0',
                      companyRecordStatus === option.value ? 'text-primary' : 'text-gray-300'
                    )}
                    aria-hidden
                  />
                  <span>
                    <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                      {option.hint}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <Users className="h-4 w-4 text-emerald-600 dark:text-emerald-300" aria-hidden />
              <h3 className="text-sm font-semibold text-gray-950 dark:text-gray-100">
                Starter people
              </h3>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <MultiSelect
                label="Managers"
                options={managerOptions}
                value={managerIds}
                onChange={setManagerIds}
                placeholder={managersPlaceholder}
                disabled={managersSelectDisabled}
              />
              <MultiSelect
                label="Initial members"
                options={userOptions}
                value={initialMemberIds}
                onChange={setInitialMemberIds}
                placeholder={
                  isCompanyScoped ? 'Use people import for existing companies' : 'Search and select users'
                }
                disabled={isCompanyScoped}
              />
            </div>
          </section>
        </div>

        <aside className="border-t border-gray-200 bg-gray-50/70 p-6 dark:border-gray-700 dark:bg-gray-900/30 lg:border-l lg:border-t-0">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-xl text-primary"
            style={{ backgroundColor: hexToRgba(companyAccent, 0.14) }}
          >
            <Building2 className="h-5 w-5" aria-hidden />
          </div>
          <h3 className="mt-4 truncate text-base font-semibold text-gray-950 dark:text-gray-100">
            {companyName || 'Untitled company'}
          </h3>
          <p className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400">
            {companyCode || suggestedCode}
          </p>
          <div className="mt-5 space-y-3 text-sm">
            {[
              ['Timezone', timezone],
              ['Work week', `${workWeek.length} days`],
              ['Status', companyRecordStatus],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3">
                <span className="text-gray-500 dark:text-gray-400">{label}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{value}</span>
              </div>
            ))}
          </div>
          <Button
            className="setup-wizard-action mt-6 w-full"
            onClick={() => saveSetup('operations')}
            disabled={!profileReady || !canUpdateSetup} loading={saveCompanySetup.isPending || updateDraft.isPending || createDraft.isPending}
          >
            Save and continue
          </Button>
        </aside>
      </div>
    </Card>
  );

  const renderOperationsStep = (): React.ReactElement => (
    <div className="space-y-5">
      <Card className="setup-wizard-material" contentClassName="p-6">
        <div className="flex items-center gap-3 border-b border-gray-200 pb-5 dark:border-gray-700">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-600 dark:bg-sky-500/20 dark:text-sky-200">
            <CalendarDays className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <h2 className="text-base font-semibold text-gray-950 dark:text-gray-100">
              Working calendar
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Set recurring work days and company holidays with explicit dates.
            </p>
          </div>
        </div>
        <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <div className="space-y-3">
            <Input
              label="Timezone"
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
              placeholder="UTC"
            />
            <MultiSelect
              label="Work week"
              options={WEEKDAY_OPTIONS}
              value={workWeek}
              onChange={setWorkWeek}
              placeholder="Select working days"
            />
            <div className="rounded-xl border border-gray-200 bg-gray-50/70 px-3 py-2.5 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-300">
              {workWeek.length > 0
                ? `${workWeek.length} working ${workWeek.length === 1 ? 'day' : 'days'} selected`
                : 'No working days selected'}
            </div>
          </div>

          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_220px_auto] sm:items-end">
              <Input
                label="Holiday name"
                value={holidayName}
                onChange={(event) => setHolidayName(event.target.value)}
                placeholder="Eid holiday"
              />
              <DatePicker label="Holiday date" value={holidayDate} onChange={setHolidayDate} />
              <Button
                type="button"
                variant="secondary"
                onClick={addHoliday}
                disabled={!holidayName.trim() || !holidayDate}
                className="setup-wizard-action h-11"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden />
                Add
              </Button>
            </div>

            <div className="min-h-[88px] overflow-hidden rounded-xl border border-gray-200 bg-white/80 dark:border-gray-700 dark:bg-gray-800/80">
              {holidays.length === 0 ? (
                <div className="flex h-full min-h-[88px] items-center gap-3 px-4 text-sm text-gray-500 dark:text-gray-400">
                  <CalendarDays className="h-4 w-4 shrink-0" aria-hidden />
                  No company holidays added yet.
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {holidays.map((holiday) => (
                    <div
                      key={holiday.date}
                      className="flex items-center justify-between gap-3 px-4 py-3"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                          {holiday.name}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          {holiday.date}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeHoliday(holiday.date)}
                        className="setup-wizard-action rounded-lg p-2 text-gray-400 transition-[transform,filter,background-color,color] hover:bg-danger/10 hover:text-danger active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/40"
                        aria-label={`Remove ${holiday.name}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button
          className="setup-wizard-action"
          onClick={() => saveSetup('import')}
          disabled={!profileReady || !canUpdateSetup} loading={saveCompanySetup.isPending}
        >
          Save operations
        </Button>
      </div>
    </div>
  );

  const renderImportStep = (): React.ReactElement => (
    <Card className="setup-wizard-material" contentClassName="p-6">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-5 dark:border-gray-700 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary dark:bg-primary-light/20 dark:text-primary-light">
            <Upload className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <h2 className="text-base font-semibold text-gray-950 dark:text-gray-100">
              People import
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Download the template, upload CSV/XLSX, map columns, and check the rows. If
              existing employees are found, choose how to handle them before importing.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => downloadTemplate.mutate()}
          disabled={!canImportSetup} loading={downloadTemplate.isPending}
          className="setup-wizard-action"
        >
          <FileSpreadsheet className="mr-2 h-4 w-4" aria-hidden />
          Employee template
        </Button>
      </div>

      {!isDraftSetup && existingCompanyMemberCount > 0 && !activeImport ? (
        <div className="mt-5 flex items-start gap-3 rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
          <div>
            <p className="font-medium text-gray-950 dark:text-gray-100">
              People import is complete
            </p>
            <p className="mt-1 text-gray-600 dark:text-gray-300">
              This company already has {existingCompanyMemberCount} active{' '}
              {existingCompanyMemberCount === 1 ? 'member' : 'members'}.
            </p>
          </div>
        </div>
      ) : null}

      <div className="mt-5 flex flex-col gap-4 rounded-xl border border-dashed border-gray-200 bg-gray-50/70 p-4 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700 dark:bg-gray-900/30">
        <div className="min-w-0">
          <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
            Upload member file
          </label>
          <input
            type="file"
            accept=".xlsx,.csv"
            className="mt-2 block text-sm text-gray-600 file:mr-4 file:h-9 file:rounded-lg file:border-0 file:bg-primary file:px-3 file:text-sm file:font-medium file:text-white dark:text-gray-300"
            disabled={!canImportMembers || createImport.isPending || uploadDraftImport.isPending}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              uploadAndPreflightImport(file);
            }}
          />
        </div>
        <Button
          variant="secondary"
          onClick={() => activeImport && setSelectedImportId(getImportId(activeImport))}
          disabled={!activeImport}
          className="setup-wizard-action"
        >
          <Upload className="mr-2 h-4 w-4" aria-hidden />
          Load latest
        </Button>
      </div>
      {!companyId && !activeDraftId ? (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          Start the setup draft first, then import members into the prepared company.
        </p>
      ) : null}

      {activeImport ? (
        <div className="mt-6 space-y-5">
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px]">
            <div className="rounded-xl border border-gray-200 bg-white/75 p-4 dark:border-gray-700 dark:bg-gray-800/70">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={statusVariant(activeImportStatus)} dot>
                      {importStepLabel(activeImportStatus, mappingDirty)}
                    </Badge>
                    {mappingDirty ? <Badge variant="warning">Unsaved changes</Badge> : null}
                  </div>
                  <p className="mt-2 truncate text-sm font-semibold text-gray-950 dark:text-gray-100">
                    {activeImportFilename}
                  </p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {activeImportCompleted
                      ? `${activeImportTotals.created} created · ${activeImportTotals.linked} added · ${activeImportTotals.updated} updated`
                      : `${mappingsReady.length} fields matched · ${mappingsToReview.length} need review`}
                  </p>
                  <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-300">
                    {importActionHint}
                  </p>
                </div>
                {activeImportCompleted ? (
                  <Button variant="secondary" onClick={() => goToStep('review')} className="setup-wizard-action">
                    Next step
                  </Button>
                ) : (
                  <Button
                    onClick={runImportPrimaryAction}
                    disabled={(canCommit ? !canImportSetup : !canCheckRows)} loading={saveMapping.isPending || validateImport.isPending || commitImport.isPending || checkDraftRows.isPending}
                    className="setup-wizard-action"
                  >
                    {canCommit && !isDraftSetup ? (
                      <Upload className="mr-2 h-4 w-4" aria-hidden />
                    ) : (
                      <ClipboardCheck className="mr-2 h-4 w-4" aria-hidden />
                    )}
                    {canCommit && !isDraftSetup ? 'Import users' : 'Check rows'}
                  </Button>
                )}
              </div>
              {hasMissingRequiredMapping ? (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-400/25 dark:bg-red-400/10 dark:text-red-200">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  Map first name, last name, and email before validation.
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-3 overflow-hidden rounded-xl border border-gray-200 bg-white/75 text-center dark:border-gray-700 dark:bg-gray-800/70 lg:grid-cols-1 lg:text-left">
              {[
                ['Rows', activeImportTotals.totalRows],
                ['Ready', importReadyCount],
                ['Blocked', invalidRowCount],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="border-r border-gray-100 px-4 py-3 last:border-r-0 dark:border-gray-700 lg:border-b lg:border-r-0 lg:last:border-b-0"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {label}
                  </p>
                  <p className="mt-1 text-lg font-semibold tabular-nums text-gray-950 dark:text-gray-100">
                    {value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div
            className={cn(
              'grid gap-4',
              hasExistingImportUsers ? 'md:grid-cols-3' : 'md:grid-cols-2'
            )}
          >
            {hasExistingImportUsers ? (
              <Select
                label="Existing employees"
                options={duplicateOptions}
                value={duplicateRule}
                onChange={(value) => {
                  setDuplicateRule(value as ImportDuplicateRule);
                  setMappingDirty(true);
                }}
                disabled={activeImportLocked}
              />
            ) : null}
            <Select
              label="Default role"
              options={roleOptions}
              value={defaultRoleId}
              onChange={(value) => {
                setDefaultRoleId(value);
                setMappingDirty(true);
              }}
              disabled={activeImportLocked}
            />
            <Select
              label="Default company"
              options={companyOptions}
              value={defaultCompanyId}
              onChange={(value) => {
                setDefaultCompanyId(value);
                setMappingDirty(true);
              }}
              disabled={isCompanyScoped || activeImportLocked}
            />
          </div>

          {activeImportCompleted ? (
            <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-400/25 dark:bg-green-400/10 dark:text-green-200">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <div>
                  <p className="font-semibold">Import completed</p>
                  <p className="mt-1">
                    {activeImportTotals.created} new users created, {activeImportTotals.linked} existing users added to this company, {activeImportTotals.updated} users updated.
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
            <div className="rounded-xl border border-gray-200 bg-white/80 dark:border-gray-700 dark:bg-gray-800/70">
              <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                <div>
                  <h3 className="text-sm font-semibold text-gray-950 dark:text-gray-100">
                    Review these matches
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Only fields that are missing or look unusual are shown here.
                  </p>
                </div>
                <Badge variant={mappingsToReview.length > 0 ? 'warning' : 'success'}>
                  {mappingsToReview.length > 0 ? `${mappingsToReview.length} to check` : 'Ready'}
                </Badge>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {(mappingsToReview.length > 0 ? mappingsToReview : mappingsReady.slice(0, 3)).map(
                  (item) => (
                    <div
                      key={item.field}
                      className="grid gap-3 px-4 py-3 md:grid-cols-[220px_minmax(0,1fr)] md:items-center"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {item.label}
                          </p>
                          {requiredImportFields.has(item.field) ? (
                            <Badge size="sm" variant="info">Required</Badge>
                          ) : null}
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {item.message}
                        </p>
                      </div>
                      <Select
                        options={headerOptions}
                        value={mapping[item.field] ?? ''}
                        searchable
                        disabled={activeImportLocked}
                        onChange={(value) => {
                          setMapping((current) => ({ ...current, [item.field]: value }));
                          setMappingDirty(true);
                        }}
                      />
                    </div>
                  )
                )}
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white/80 p-4 dark:border-gray-700 dark:bg-gray-800/70">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
                <h3 className="text-sm font-semibold text-gray-950 dark:text-gray-100">
                  Matched fields
                </h3>
              </div>
              <div className="mt-3 space-y-2">
                {mappingsReady.slice(0, 6).map((item) => (
                  <div key={item.field} className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate text-gray-600 dark:text-gray-300">{item.label}</span>
                    <span className="truncate font-medium text-gray-950 dark:text-gray-100">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
              {optionalMappings.length > 0 ? (
                <div className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
                  {optionalMappings.length} optional fields can stay unmapped.
                </div>
              ) : null}
            </div>
          </div>

          <details className="rounded-xl border border-gray-200 bg-white/75 dark:border-gray-700 dark:bg-gray-800/70">
            <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-gray-900 marker:text-gray-400 dark:text-gray-100">
              All column mappings
            </summary>
            <div className="border-t border-gray-100 p-4 dark:border-gray-700">
              <div className="grid gap-3 md:grid-cols-2">
                {mappingInsights.map((item) => (
                  <div key={item.field} className="grid gap-2">
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                      {item.label}
                    </label>
                    <Select
                      options={headerOptions}
                      value={mapping[item.field] ?? ''}
                      searchable
                      disabled={activeImportLocked}
                      onChange={(value) => {
                        setMapping((current) => ({ ...current, [item.field]: value }));
                        setMappingDirty(true);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </details>

          {previewRows.length > 0 ? (
            <DataTable caption="Import preview">
              <DataTableHead>
                <tr>
                  <DataTableHeadCell>Row</DataTableHeadCell>
                  <DataTableHeadCell>Name</DataTableHeadCell>
                  <DataTableHeadCell>Email</DataTableHeadCell>
                  <DataTableHeadCell>Role</DataTableHeadCell>
                  <DataTableHeadCell>Company</DataTableHeadCell>
                  <DataTableHeadCell>Status</DataTableHeadCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {previewRows.slice(0, 5).map((row) => {
                  const rowResult = rowResultsByRow.get(row.row);
                  return (
                    <DataTableRow key={row.row}>
                      <DataTableCell>{row.row}</DataTableCell>
                      <DataTableCell noWrap={false}>
                        {[getMappedHeaderValue(row, 'firstName', mapping), getMappedHeaderValue(row, 'lastName', mapping)]
                          .filter(Boolean)
                          .join(' ') || 'Not mapped'}
                      </DataTableCell>
                      <DataTableCell noWrap={false}>
                        {getMappedHeaderValue(row, 'email', mapping) || 'Not mapped'}
                      </DataTableCell>
                      <DataTableCell noWrap={false}>
                        {getMappedHeaderValue(row, 'role', mapping) || 'Default role'}
                      </DataTableCell>
                      <DataTableCell noWrap={false}>
                        {getMappedHeaderValue(row, 'companyCode', mapping) || 'Default company'}
                      </DataTableCell>
                      <DataTableCell noWrap={false}>
                        <Badge variant={rowResultVariant(rowResult?.status)}>
                          {rowResultLabel(rowResult?.status)}
                        </Badge>
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </DataTableBody>
            </DataTable>
          ) : null}

          {activeSetAsideRows.length > 0 ? (
            <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200">
              <div className="flex items-start gap-3">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" aria-hidden />
                <p>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {activeSetAsideRows.length} {activeSetAsideRows.length === 1 ? 'row' : 'rows'} set aside
                  </span>{' '}
                  {activeImportCompleted
                    ? 'were not imported. Download them with their problems, fix them, and import them as a new file.'
                    : 'won’t be imported. Download them with their problems to fix and import later, or put them back.'}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={downloadSetAsideRows}
                  loading={downloadSetAside.isPending}
                >
                  <Download className="h-3.5 w-3.5" aria-hidden />
                  Download
                </Button>
                {!activeImportLocked ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => changeSetAside('restore-set-aside')}
                    loading={importSetAside.isPending && importSetAside.variables?.action === 'restore-set-aside'}
                    disabled={importSetAside.isPending}
                  >
                    Put back
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}

          {activeImportErrors.length > 0 ? (
            <div className="rounded-xl border border-red-200 bg-red-50/70 dark:border-red-400/25 dark:bg-red-400/10">
              <div className="flex flex-col gap-3 border-b border-red-100 px-4 py-3 sm:flex-row sm:items-start sm:justify-between dark:border-red-400/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-300" aria-hidden />
                  <div>
                    <h3 className="text-sm font-semibold text-red-900 dark:text-red-100">
                      {failingRowCount} {failingRowCount === 1 ? 'row needs' : 'rows need'} fixing before {failingRowCount === 1 ? 'it is' : 'they are'} imported
                    </h3>
                    <p className="mt-1 text-sm text-red-800/80 dark:text-red-200/80">
                      {canSetAsideRows
                        ? 'Correct the source file and upload it again, or set these rows aside and import the rest now.'
                        : 'Correct the source file, upload it again, or adjust the mapping above.'}
                    </p>
                  </div>
                </div>
                {canSetAsideRows ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="shrink-0 self-start"
                    onClick={() => changeSetAside('set-aside-failed')}
                    loading={importSetAside.isPending && importSetAside.variables?.action === 'set-aside-failed'}
                    disabled={importSetAside.isPending}
                  >
                    Set aside {failingRowCount} {failingRowCount === 1 ? 'row' : 'rows'}
                  </Button>
                ) : null}
              </div>
              <DataTable
                caption="Import validation errors"
                className="border-0 bg-transparent shadow-none dark:bg-transparent"
              >
                <DataTableHead className="bg-red-50/80 dark:bg-red-400/5">
                  <tr>
                    <DataTableHeadCell>Row</DataTableHeadCell>
                    <DataTableHeadCell>Field</DataTableHeadCell>
                    <DataTableHeadCell>Problem</DataTableHeadCell>
                  </tr>
                </DataTableHead>
                <DataTableBody>
                  {activeImportErrors.map((error, index) => (
                    <DataTableRow key={`${error.row}-${error.field ?? index}`}>
                      <DataTableCell>{error.row}</DataTableCell>
                      <DataTableCell>{error.field ? labelForImportField(error.field as ImportFieldKey) : 'Row'}</DataTableCell>
                      <DataTableCell noWrap={false}>{error.message}</DataTableCell>
                    </DataTableRow>
                  ))}
                </DataTableBody>
              </DataTable>
            </div>
          ) : null}

          {activeImportStatus === 'validated' && activeImportErrors.length === 0 ? (
            <div className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-400/25 dark:bg-green-400/10 dark:text-green-200">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {isDraftSetup
                ? 'Rows are checked. These people will be created or added when you launch the company.'
                : 'Rows are checked. Use Import users to add these people to the system.'}
            </div>
          ) : activeImportCompleted ? null : activeImportStatus === 'validation_failed' ? (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-400/25 dark:bg-amber-400/10 dark:text-amber-200">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              Fix the blocked rows or upload a corrected file, then check rows again.
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-400/25 dark:bg-blue-400/10 dark:text-blue-200">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              Check rows before importing. The preview will show which emails are new and which already exist.
            </div>
          )}
        </div>
      ) : (
        <div className="mt-6 rounded-xl border border-gray-200 bg-white/70 p-5 dark:border-gray-700 dark:bg-gray-800/70">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-950 dark:text-gray-100">
                {peoplePrepared
                  ? 'People are already prepared'
                  : 'No import file added yet'}
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {peoplePrepared
                  ? 'Continue to review, or upload a file if you want to add more people now.'
                  : 'Use the template, upload a CSV/XLSX file, then check rows before importing.'}
              </p>
            </div>
            {peoplePrepared ? (
              <Button
                type="button"
                className="setup-wizard-action shrink-0"
                onClick={() => saveSetup('review')}
                disabled={!canUpdateSetup} loading={saveCompanySetup.isPending || updateDraft.isPending}
              >
                Continue to review
                <ChevronRight className="ml-2 h-4 w-4" aria-hidden />
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </Card>
  );

  const renderReviewStep = (): React.ReactElement => {
    const reviewItems = isDraftSetup ? wizardProgressItems : checklist;
    const summaryItems = [
      ['Company', companyName || 'Untitled company', companyCode || 'Code not set'],
      ['Settings', timezone, `${workWeek.length} work days · ${holidays.length} holidays`],
      [
        'People',
        existingCompanyMemberCount > 0
          ? `${existingCompanyMemberCount} active members`
          : `${managerIds.length} managers · ${initialMemberIds.length} members`,
        `${completedImports.length + draftImports.length} import files`,
      ],
      [
        'Import rows',
        `${draftImportTotals.newUsers} new · ${draftImportTotals.existingUsers} existing`,
        `${draftImportTotals.blockedRows} blocked`,
      ],
      [
        'Company details',
        `${profileCompleteness.completed} of ${profileCompleteness.total} added`,
        profileCompleteness.missing.length === 0
          ? 'All details filled in'
          : `Optional · ${profileCompleteness.missing.length} still empty`,
      ],
    ];

    return (
      <Card className="setup-wizard-material" contentClassName="p-0">
        <div className="flex flex-col gap-4 border-b border-gray-200 p-6 dark:border-gray-700 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200">
              <ShieldCheck className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-950 dark:text-gray-100">
                Launch review
              </h2>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
                Review the final setup package before it becomes live in the workspace.
              </p>
            </div>
          </div>
          <Badge variant={reviewWarnings.length === 0 ? 'success' : 'warning'}>
            {reviewWarnings.length === 0 ? 'Ready to launch' : `${reviewWarnings.length} to check`}
          </Badge>
        </div>

        <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="p-6">
            <div className="grid gap-3 sm:grid-cols-2">
              {summaryItems.map(([label, value, detail]) => (
                <div
                  key={label}
                  className="rounded-xl border border-gray-200 bg-gray-50/70 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/30"
                >
                  <p className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    {label}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-gray-950 dark:text-gray-100">
                    {value}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{detail}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 space-y-3">
              {reviewWarnings.length === 0 ? (
                <div className="flex items-start gap-3 rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
                  <span>Setup looks ready to launch.</span>
                </div>
              ) : (
                reviewWarnings.map((warning) => (
                  <div
                    key={warning}
                    className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100"
                  >
                    <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>{warning}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          <aside className="border-t border-gray-200 bg-gray-50/70 p-6 dark:border-gray-700 dark:bg-gray-900/30 lg:border-l lg:border-t-0">
            <h3 className="text-sm font-semibold text-gray-950 dark:text-gray-100">
              Checklist
            </h3>
            {statusLoading || draftLoading ? (
              <SectionLoader variant="list" rows={4} label="Loading checklist" className="mt-4" />
            ) : (
              <div className="mt-4 space-y-3">
                {reviewItems.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No checklist data yet.</p>
                ) : (
                  reviewItems.map((item) => (
                    <div key={item.key} className="flex items-center justify-between gap-3 text-sm">
                      <span className="truncate text-gray-600 dark:text-gray-300">{item.label}</span>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 font-medium',
                          item.completed
                            ? 'text-emerald-700 dark:text-emerald-300'
                            : 'text-gray-500 dark:text-gray-400'
                        )}
                      >
                        <CheckCircle2
                          className={cn(
                            'h-3.5 w-3.5',
                            item.completed
                              ? 'text-emerald-600 dark:text-emerald-300'
                              : 'text-gray-300 dark:text-gray-600'
                          )}
                          aria-hidden
                        />
                        {item.completed ? 'Done' : 'Pending'}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
            <div className="mt-6 flex flex-col gap-2">
              <Button
                className="setup-wizard-action w-full"
                onClick={() => saveSetup(undefined, true)}
                disabled={!canLaunchCompany} loading={saveCompanySetup.isPending || updateDraft.isPending || launchDraft.isPending}
              >
                {isDraftSetup ? 'Launch company' : 'Finish setup'}
              </Button>
              <Button variant="secondary" className="w-full" onClick={() => goToStep('company')}>
                Edit company
              </Button>
            </div>
          </aside>
        </div>
      </Card>
    );
  };

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Companies', to: '/companies' },
        ...(company ? [{ label: company.name, to: `/companies/${company._id}` }] : []),
        { label: 'Setup Wizard' },
      ]}
      icon={Wand2}
      title={company ? `${company.name} setup` : 'New company setup'}
      description={
        company
          ? 'Review configuration and import members for this company.'
          : 'Create a company, configure its baseline, and import initial members.'
      }
      className="gap-0"
      hideHeader
    >
      <WorkspaceToolbar
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate('/companies')}>
            <ChevronLeft className="mr-1.5 h-4 w-4" aria-hidden />
            Cancel
          </Button>
        }
      />

      <div className="setup-wizard-page min-w-0 space-y-5 pb-24 lg:pb-6">
        <div className="setup-wizard-material rounded-2xl border border-gray-200/80 p-3 shadow-sm dark:border-gray-700/80">
          <div className="grid min-w-0 gap-2 md:grid-cols-5">
            {wizardSteps.map((step, index) => {
              const locked = Boolean(getStepLockReason(step.id));
              const state =
                stepCompletedById[step.id] && step.id !== activeStep
                  ? 'done'
                  : index === activeStepIndex
                    ? 'current'
                    : 'pending';
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => goToStep(step.id)}
                  aria-disabled={locked}
                  className={cn(
                    'setup-wizard-action flex min-h-16 min-w-0 items-center gap-3 rounded-xl px-3 py-2 text-left transition-[transform,filter,background-color,color] active:scale-[0.99]',
                    state === 'current'
                      ? 'bg-primary text-white shadow-sm'
                      : locked
                        ? 'cursor-not-allowed text-gray-400 opacity-65 dark:text-gray-500'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                  )}
                >
                  <span
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                      state === 'current'
                        ? 'bg-white/20 text-white'
                        : state === 'done'
                          ? 'bg-success/10 text-success'
                          : locked
                            ? 'bg-gray-100 text-gray-300 dark:bg-gray-800 dark:text-gray-600'
                            : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                    )}
                  >
                    <StepIcon state={state} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-semibold uppercase">{step.eyebrow}</span>
                    <span className="block truncate text-sm font-semibold">{step.label}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
          <main className="min-w-0">
            <div key={activeStep} className="setup-wizard-step-panel">
              {activeStep === 'guide' ? renderGuideStep() : null}
              {activeStep === 'company' ? renderCompanyStep() : null}
              {activeStep === 'operations' ? renderOperationsStep() : null}
              {activeStep === 'import' ? renderImportStep() : null}
              {activeStep === 'review' ? renderReviewStep() : null}
            </div>
          </main>

          <aside className="min-w-0 space-y-5 xl:sticky xl:top-24 xl:self-start">
            <Card className="setup-wizard-material" contentClassName="p-4 sm:p-5">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
                  <Sparkles className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                    {activeStepConfig.eyebrow}
                  </p>
                  <h2 className="mt-1 text-base font-semibold text-gray-950 dark:text-gray-100">
                    {activeStepConfig.title}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                    {activeStepConfig.guide}
                  </p>
                </div>
              </div>
              <div className="mt-4 rounded-xl border border-gray-200 bg-white/65 px-4 py-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/65 dark:text-gray-300">
                <span className="font-medium text-gray-950 dark:text-gray-100">Outcome: </span>
                {activeStepConfig.outcome}
              </div>
            </Card>

            <Card className="setup-wizard-material" contentClassName="p-4 sm:p-5">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                <h2 className="min-w-0 truncate text-sm font-semibold text-gray-950 dark:text-gray-100">
                  Setup readiness
                </h2>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {progressCompleted}/{progressTotal}
                </span>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-300"
                  style={{ width: `${Math.min(100, Math.max(0, progressPercent))}%` }}
                />
              </div>
              <div className="mt-4 space-y-2 text-sm">
                {wizardProgressItems.map((item) => (
                  <div
                    key={item.key}
                    className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3"
                  >
                    <span className="min-w-0 truncate text-gray-500 dark:text-gray-400">{item.label}</span>
                    <span
                      className={cn(
                        'inline-flex shrink-0 items-center gap-1.5 font-medium',
                        item.completed
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : 'text-gray-500 dark:text-gray-400'
                      )}
                    >
                      <CheckCircle2
                        className={cn(
                          'h-3.5 w-3.5',
                          item.completed
                            ? 'text-emerald-600 dark:text-emerald-300'
                            : 'text-gray-300 dark:text-gray-600'
                        )}
                        aria-hidden
                      />
                      {item.completed ? 'Done' : 'Pending'}
                    </span>
                  </div>
                ))}
                <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
                  <div className="grid min-w-0 grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] items-center gap-3">
                    <span className="min-w-0 truncate text-gray-500 dark:text-gray-400">Company</span>
                    <span className="min-w-0 truncate text-right font-medium text-gray-900 dark:text-gray-100">
                      {companyName || 'Not named'}
                    </span>
                  </div>
                  <div className="mt-2 grid min-w-0 grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] items-center gap-3">
                    <span className="min-w-0 truncate text-gray-500 dark:text-gray-400">People</span>
                    <span className="min-w-0 truncate text-right font-medium text-gray-900 dark:text-gray-100">
                      {existingCompanyMemberCount > 0
                        ? `${existingCompanyMemberCount} active`
                        : `${initialMemberIds.length + managerIds.length} direct · ${completedImports.length} imports`}
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {imports.length > 0 ? (
              <Card className="setup-wizard-material" contentClassName="p-4 sm:p-5">
                <h2 className="truncate text-sm font-semibold text-gray-950 dark:text-gray-100">
                  Import history
                </h2>
                <div className="mt-4 space-y-3">
                  {imports.slice(0, 4).map((job) => (
                    <button
                      key={job._id}
                      type="button"
                      onClick={() => {
                        setSelectedImportId(job._id);
                        goToStep('import');
                      }}
                      className="setup-wizard-action block min-w-0 w-full rounded-lg border border-gray-200 px-3 py-2 text-left text-sm transition-[transform,filter,background-color] hover:bg-gray-50 active:scale-[0.99] dark:border-gray-700 dark:hover:bg-gray-700/40"
                    >
                      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                        <span className="min-w-0 truncate font-medium text-gray-900 dark:text-gray-100">
                          {job.originalFilename}
                        </span>
                        <Badge size="sm" variant={statusVariant(job.status)} className="shrink-0">
                          {job.status}
                        </Badge>
                      </div>
                      <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
                        {importTotals(job).totalRows} rows · {importTotals(job).created} created
                      </p>
                    </button>
                  ))}
                </div>
              </Card>
            ) : null}

          </aside>
        </div>

        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200/80 bg-white/85 px-4 py-3 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur-xl dark:border-gray-700/80 dark:bg-gray-900/85 lg:hidden">
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => goToStep(wizardSteps[Math.max(activeStepIndex - 1, 0)].id)}
              disabled={activeStepIndex === 0}
            >
              Back
            </Button>
            {activeStep === 'review' ? (
              <Button
                size="sm"
                onClick={() => saveSetup(undefined, true)}
                disabled={!canLaunchCompany} loading={saveCompanySetup.isPending || updateDraft.isPending || launchDraft.isPending}
              >
                {isDraftSetup ? 'Launch company' : 'Finish setup'}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() =>
                  goToStep(wizardSteps[Math.min(activeStepIndex + 1, wizardSteps.length - 1)].id)
                }
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
